import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { menuService, getSchoolUsers, inviteUserToSchool, updateUserRoles, removeUserFromSchool } from '../utils/api';

/**
 * Custom hook for fetching school details
 */
export const useSchoolDetails = (schoolId) => {
  return useQuery({
    queryKey: ['schoolDetails', schoolId],
    queryFn: () => menuService.getSchoolDetails(schoolId),
    enabled: !!schoolId,
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
};

/**
 * Custom hook for updating school details with optimistic updates
 */
export const useUpdateSchool = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ schoolId, ...schoolData }) => 
      menuService.updateSchool(schoolId, schoolData),
    
    onMutate: async ({ schoolId, ...schoolData }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: ['schoolDetails', schoolId],
      });

      // Snapshot the previous value
      const previousSchool = queryClient.getQueryData(['schoolDetails', schoolId]);

      // Optimistically update the school details
      if (previousSchool) {
        queryClient.setQueryData(
          ['schoolDetails', schoolId],
          { ...previousSchool, ...schoolData }
        );
      }

      return { previousSchool };
    },
    
    onError: (err, { schoolId }, context) => {
      // If the mutation fails, revert to the previous value
      if (context?.previousSchool) {
        queryClient.setQueryData(
          ['schoolDetails', schoolId],
          context.previousSchool
        );
      }
    },
    
    onSettled: (data, error, { schoolId }) => {
      // Refetch to ensure the local data is in sync with the server
      queryClient.invalidateQueries({
        queryKey: ['schoolDetails', schoolId],
      });
    },
  });
};

/**
 * Custom hook for fetching a school by subdomain
 */
export const useSchoolBySubdomain = (subdomain) => {
  return useQuery({
    queryKey: ['schoolBySubdomain', subdomain],
    queryFn: () => menuService.getSchoolBySubdomain(subdomain),
    enabled: !!subdomain,
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
};

/**
 * Custom hook for fetching user's schools
 */
export const useUserSchools = (userId) => {
  return useQuery({
    queryKey: ['userSchools', userId],
    queryFn: async () => {
      console.log(`[useUserSchools] Fetching schools for user ${userId || 'current'}`);
      try {
        const start = Date.now();
        const schools = await menuService.getUserSchools(userId);
        const elapsed = Date.now() - start;
        
        console.log(`[useUserSchools] Fetched ${schools.length} schools in ${elapsed}ms`);
        
        if (schools.length === 0) {
          console.warn('[useUserSchools] No schools found for user - this might be an error');
        }
        
        return schools;
      } catch (error) {
        console.error('[useUserSchools] Error fetching schools:', error);
        throw error; // Re-throw to let React Query handle retries
      }
    },
    enabled: true, // Always fetch even if userId is not provided
    staleTime: 1000 * 60 * 5, // Increase stale time to 5 minutes to prevent frequent refetching
    cacheTime: 1000 * 60 * 10, // 10 minutes
    refetchOnWindowFocus: false, // Changed to false to prevent frequent refreshes
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchInterval: false, // Only refresh on explicit actions, not on a timer
    retry: 2, // Reduce retry attempts
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 10000), // Exponential backoff
    onError: (error) => {
      console.error('[useUserSchools] Error in query:', error);
    }
  });
};

/**
 * Custom hook for fetching school users
 */
export const useSchoolUsers = (schoolId) => {
  return useQuery({
    queryKey: ['schoolUsers', schoolId],
    queryFn: () => getSchoolUsers(schoolId),
    enabled: !!schoolId,
    staleTime: 0, // Always consider the data stale to prevent caching issues
    refetchOnWindowFocus: true, // Refetch when window gains focus
    refetchOnMount: true, // Ensure refetch when component mounts
  });
};

/**
 * Custom hook for inviting a user to a school with optimistic updates
 */
export const useInviteUserToSchool = (schoolId) => {
  const queryClient = useQueryClient();
  const schoolUsersQuery = useSchoolUsers(schoolId);

  return useMutation({
    mutationFn: (data) => {
      // Extract email and roles from data
      const email = data.email;
      const roles = data.roles;
      // Get schoolId from either the parameter object or the hook parameter
      const actualSchoolId = data.schoolId ? data.schoolId : schoolId;
      
      return inviteUserToSchool(actualSchoolId, email, roles);
    },
    onMutate: async (newUser) => {
      // Extract values for optimistic update
      const email = newUser.email;
      const roles = newUser.roles;
      
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: ['schoolUsers', schoolId] });

      // Snapshot the previous value
      const previousUsers = queryClient.getQueryData(['schoolUsers', schoolId]);

      // Optimistically update to the new value
      queryClient.setQueryData(['schoolUsers', schoolId], (old) => {
        const optimisticUser = {
          email,
          roles,
          id: 'temp-id-' + Date.now(), // Temporary ID for the optimistic update
          status: 'pending'
        };
        return old && Array.isArray(old) ? [...old, optimisticUser] : [optimisticUser];
      });

      return { previousUsers };
    },
    onError: (err, newUser, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context && context.previousUsers) {
        queryClient.setQueryData(['schoolUsers', schoolId], context.previousUsers);
      }
      console.error('Error inviting user:', err);
    },
    onSettled: () => {
      // Invalidate and force refetch to ensure fresh data
      queryClient.invalidateQueries({
        queryKey: ['schoolUsers', schoolId],
        refetchType: 'all', // Force a refetch from the server
        exact: true // Only invalidate this exact query
      });
    },
  });
};

/**
 * Custom hook for updating user roles with optimistic updates
 */
export const useUpdateUserRoles = (schoolId) => {
  const queryClient = useQueryClient();
  const schoolUsersQuery = useSchoolUsers(schoolId);

  return useMutation({
    mutationFn: (data) => {
      // Extract userId and roles
      const userId = data.userId;
      const roles = data.roles;
      // Get schoolId from either the parameter object or the hook parameter
      const actualSchoolId = data.schoolId ? data.schoolId : schoolId;
      
      return updateUserRoles(userId, actualSchoolId, roles);
    },
    onMutate: async (variables) => {
      // Extract variables
      const userId = variables.userId;
      const roles = variables.roles;
      
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['schoolUsers', schoolId] });

      // Snapshot the previous value
      const previousUsers = queryClient.getQueryData(['schoolUsers', schoolId]);

      // Optimistically update to the new value
      queryClient.setQueryData(['schoolUsers', schoolId], (old) => {
        return old && Array.isArray(old) 
          ? old.map(user => {
              if (user.id === userId) {
                return { ...user, roles };
              }
              return user;
            })
          : [];
      });

      return { previousUsers };
    },
    onError: (err, variables, context) => {
      // If the mutation fails, roll back
      if (context && context.previousUsers) {
        queryClient.setQueryData(['schoolUsers', schoolId], context.previousUsers);
      }
      console.error('Error updating user roles:', err);
    },
    onSettled: () => {
      // Invalidate and force refetch to ensure fresh data
      queryClient.invalidateQueries({
        queryKey: ['schoolUsers', schoolId],
        refetchType: 'all', // Force a refetch from the server
        exact: true // Only invalidate this exact query
      });
    },
  });
};

/**
 * Custom hook for removing a user from a school with optimistic updates
 */
export const useRemoveUserFromSchool = (schoolId) => {
  const queryClient = useQueryClient();
  const schoolUsersQuery = useSchoolUsers(schoolId);

  return useMutation({
    mutationFn: (data) => {
      // Extract userId if it's an object, otherwise use it directly
      const userId = typeof data === 'object' ? data.userId : data;
      // Get schoolId from either the parameter object or the hook parameter
      const actualSchoolId = (typeof data === 'object' && data.schoolId) ? data.schoolId : schoolId;
      
      return removeUserFromSchool(userId, actualSchoolId);
    },
    onMutate: async (userId) => {
      // Extract userId if it's an object, otherwise use it directly
      const userIdToRemove = typeof userId === 'object' ? userId.userId : userId;
      
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['schoolUsers', schoolId] });

      // Snapshot the previous value
      const previousUsers = queryClient.getQueryData(['schoolUsers', schoolId]);

      // Optimistically update to the new value
      queryClient.setQueryData(['schoolUsers', schoolId], (old) => {
        return old && Array.isArray(old) ? old.filter(user => user.id !== userIdToRemove) : [];
      });

      return { previousUsers };
    },
    onError: (err, userId, context) => {
      // If the mutation fails, roll back
      if (context && context.previousUsers) {
        queryClient.setQueryData(['schoolUsers', schoolId], context.previousUsers);
      }
      console.error('Error removing user:', err);
    },
    onSettled: () => {
      // Invalidate and force refetch to ensure fresh data
      queryClient.invalidateQueries({
        queryKey: ['schoolUsers', schoolId],
        refetchType: 'all', // Force a refetch from the server
        exact: true // Only invalidate this exact query
      });
    },
  });
}; 